import { memo, useCallback, useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';
import Slider from 'react-slick';
import { useRouter } from 'next/router';
import { MATCH_STATUS } from '@zmedia/bm-soccer-ui/constants';
import { IMatch } from '@zmedia/bm-soccer-ui/interface';
import { ArrayUtils, TimeUtils } from '@zmedia/bm-soccer-ui/utils';
import { getDateInText, unixToDate } from '@zmedia/bm-soccer-ui/utils/date';
import {
    Element,
    Icon,
    IconButton,
    RouterLink,
    Section,
} from '@zmedia/bm-soccer-ui/components';
import { URL_SOCCER_PAGE } from '@zmedia/bm-soccer-ui/modules/shared';
import Empty from '@zmedia/bm-soccer-ui/modules/shared/components/Empty';
import { Score, Team } from '@zmedia/bm-soccer-ui/modules/match';
import { TitleHeader } from '@zmedia/bm-soccer-ui/modules/shared/components/TitleHeader';
import { TitleWrap } from '@zmedia/bm-soccer-ui/modules/shared/components/TitleWrap';
import styles from './style.module.scss';

const RANGE_DATE = 3;

export function getSliceMatch(items: IMatch[]) {
    if (TimeUtils.currentSecords() > items[items.length - 1]?.startTime) {
        // tournament ket thuc thi lay 15 item cuoi cung
        return items.slice(-15).reverse();
    }
    const group = ArrayUtils.groupByDate(items, 'DD/MM/YYYY');

    const currentDate = unixToDate(TimeUtils.currentSecords(), 'DD/MM/YYYY');
    let currentIndex = Object.keys(group).indexOf(currentDate);

    if (currentIndex === -1) {
        // ngay hien tai co tran dau
        // ngay hien tai la ngay cua tran dau NOT_STARTED gan nhat
        const _items = items.filter((item) => {
            return item.status.id === MATCH_STATUS.NOT_STARTED;
        });

        const currentDate = unixToDate(_items[0]?.startTime, 'DD/MM/YYYY');
        currentIndex = Object.keys(group).indexOf(currentDate);
    }

    const futureDate = Object.keys(group).filter(
        (_item, index) =>
            index - currentIndex <= RANGE_DATE && index - currentIndex >= 0
    );

    const passDate = Object.keys(group).filter(
        (_item, index) =>
            currentIndex - index < RANGE_DATE && currentIndex - index > 0
    );

    // lay cac tran theo thu tu (ngay hien tai + RANGE_DATE ngay tiep theo + RANGE_DATE ngay da qua)
    const _items = [...futureDate, ...passDate].reduce((item: any, index) => {
        return [...item, ...group[index]];
    }, []);
    return _items;
}

export function SliderMatch(props: any) {
    const router = useRouter();
    const items: IMatch[] = props.items || [];
    const size = useWindowSize();
    const [slider, setSlider] = useState<number>(1);
    const renderItem = (item: any, index: number) => {
        return (
            <Element className={styles['match-item']} key={index}>
                <RouterLink
                    to={`${URL_SOCCER_PAGE.SUBPATH}${item.path}`}
                    tabIndex={-1}
                    title={
                        item.home.team.name + ' đấu với ' + item.away.team.name
                    }
                >
                    <Team team={item.home.team} />
                    <Element className={styles['match-info']}>
                        {getDateInText(
                            unixToDate(item.startTime, 'YYYY-MM-DD'),
                            'DD/MM/YYYY'
                        )}
                        <Score item={item}></Score>
                    </Element>
                    <Team team={item.away.team} />
                </RouterLink>
            </Element>
        );
    };

    const renderItems = useCallback(() => {
        return _items.map((item: any, index: number) => {
            return renderItem(item, index);
        });
    }, [items]);

    useEffect(() => {
        if (size.width <= 768) {
            setSlider(1);
        } else {
            setSlider(2);
        }
    }, [size]);

    const settings = {
        slidesToShow: slider,
        slidesToScroll: slider,
        dots: false,
        infinite: items.length > 3,
        speed: 500,
        autoplaySpeed: 4000,
        draggable: false,

        nextArrow: <IconButton icon="forward-2" title="Sau"></IconButton>,
        prevArrow: <IconButton icon="back-2" title="Trước"></IconButton>,
    };

    if (ArrayUtils.isEmpty(items)) {
        return (
            <Section className={styles['slider-section']}>
                <TitleHeader>
                    <TitleWrap
                        renderAs="h2"
                        title={`Lịch Thi Đấu ${props.name}`}
                    ></TitleWrap>
                </TitleHeader>
                <Empty content="Chưa có trận đấu nào"></Empty>
            </Section>
        );
    }

    const _items = getSliceMatch(items) || [];
    const seasonId = router.query.seasonId;

    return (
        <Section className={styles['slider-section']}>
            <TitleHeader>
                <TitleWrap
                    renderAs="h2"
                    title={`Lịch Thi Đấu ${props.name}`}
                ></TitleWrap>
                <TitleWrap
                    title="Xem tất cả"
                    link={`${URL_SOCCER_PAGE.SUBPATH}lich-thi-dau-${
                        props.path
                    }${seasonId ? `?seasonId=${seasonId}` : ''}`}
                >
                    <Icon icon="forward" />
                </TitleWrap>
            </TitleHeader>
            <Element className={styles['slider-container']}>
                <Element className={styles['slider-match']}>
                    <Slider {...settings}>{renderItems()}</Slider>
                </Element>
            </Element>
        </Section>
    );
}

export default memo(SliderMatch);
